import {
  Box,
  Button,
  Fade,
  Typography,
  OutlinedInput,
  FormControl,
  FormHelperText,
  CircularProgress,
} from '@mui/material'
import { useState, useRef } from 'react'
import { signInWithOTP, sendSignInLinkWithOTP } from '../lib/auth'
import SSOButtons from './SSOButtons'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const OTP_LENGTH = 6

interface OTPVerificationProps {
  onClick: () => void
  email: string
  emailAutosent?: boolean
}

enum OTPStatus {
  Idle = 'idle',
  Submitting = 'submitting',
  Error = 'error',
}

export default function OTPVerification({
  onClick,
  email,
  emailAutosent,
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [status, setStatus] = useState<OTPStatus>(OTPStatus.Idle)
  const [errorMessage, setErrorMessage] = useState('')
  const [isResending, setIsResending] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  function focusInput(index: number) {
    setTimeout(() => {
      const input = inputRefs.current[index]
      if (input) {
        input.focus()
      }
    }, 10)
  }

  function handleMultipleDigits(digits: string[]) {
    const newOtp = [...otp]
    for (let i = 0; i < OTP_LENGTH; i++) {
      newOtp[i] = digits[i] || ''
    }
    setOtp(newOtp)

    const nextIndex = Math.min(digits.length, OTP_LENGTH - 1)
    focusInput(nextIndex)
  }

  function handleSingleDigit(index: number, digit: string) {
    const newOtp = [...otp]
    newOtp[index] = digit
    setOtp(newOtp)

    if (digit && index < OTP_LENGTH - 1) {
      focusInput(index + 1)
    }
  }

  function clearErrorState() {
    if (status === OTPStatus.Error) {
      setStatus(OTPStatus.Idle)
      setErrorMessage('')
    }
  }

  function handleOtpChange(index: number, value: string) {
    const numericValue = value.replace(/\D/g, '')

    if (numericValue.length > 1) {
      const digits = numericValue.split('').slice(0, OTP_LENGTH)
      handleMultipleDigits(digits)
    } else {
      const digit = numericValue.slice(-1)
      handleSingleDigit(index, digit)
    }

    clearErrorState()
  }

  function handleKeyDown(
    index: number,
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    if (event.key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp]
        newOtp[index] = ''
        setOtp(newOtp)
      } else if (index > 0) {
        const newOtp = [...otp]
        newOtp[index - 1] = ''
        setOtp(newOtp)
        focusInput(index - 1)
      }
    }
  }

  function handlePaste(event: React.ClipboardEvent) {
    event.preventDefault()
    const pastedData = event.clipboardData.getData('text')
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, OTP_LENGTH)

    const newOtp = [...otp]
    for (let i = 0; i < OTP_LENGTH; i++) {
      newOtp[i] = digits[i] || ''
    }

    setOtp(newOtp)

    const nextIndex = Math.min(digits.length, OTP_LENGTH - 1)
    focusInput(nextIndex)
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()

    const otpString = otp.join('')
    if (otpString.length !== OTP_LENGTH) {
      setStatus(OTPStatus.Error)
      setErrorMessage(`Please enter a valid ${OTP_LENGTH}-digit OTP code`)
      return
    }

    setStatus(OTPStatus.Submitting)
    setErrorMessage('')

    try {
      await signInWithOTP(email, otpString)
    } catch (error) {
      setStatus(OTPStatus.Error)
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Verification failed. Please try again.',
      )
      setOtp(['', '', '', '', '', ''])
      focusInput(0)
    }
  }

  async function handleResendCode() {
    setIsResending(true)
    try {
      const success = await sendSignInLinkWithOTP(email)
      if (success) {
        setErrorMessage('')
        setStatus(OTPStatus.Idle)
      } else {
        setErrorMessage('Failed to send new code. Please try again.')
      }
    } catch {
      setErrorMessage('Failed to send new code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          maxWidth: 400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          gutterBottom
          variant='h2'
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          Check your email
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography variant='subtitle1' textAlign='center' sx={{ mb: 2 }}>
            We&apos;ve sent an email to {email}. Click the link in the email to
            login, or enter the code below.
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Verification Code
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                mb: 1,
              }}
              onPaste={handlePaste}
            >
              {otp.map((digit, index) => (
                <OutlinedInput
                  key={index}
                  inputRef={(el: HTMLInputElement | null) => {
                    inputRefs.current[index] = el
                  }}
                  type='tel'
                  inputMode='numeric'
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    pattern: '[0-9]*',
                    style: { textAlign: 'center' },
                  }}
                  sx={{
                    width: '48px',
                    height: '48px',
                    '& .MuiOutlinedInput-input': {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                    },
                  }}
                  autoFocus={index === 0}
                />
              ))}
            </Box>
            {status === OTPStatus.Error && (
              <FormControl fullWidth error>
                <FormHelperText
                  sx={{ color: 'error.main', textAlign: 'center' }}
                >
                  {errorMessage}
                  {errorMessage === 'Invalid OTP code. Please try again.' && (
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant='text'
                        size='small'
                        onClick={handleResendCode}
                        disabled={isResending}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          color: 'primary.main',
                          textDecoration: 'underline',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {isResending ? 'Sending...' : 'Get a new code'}
                      </Button>
                    </Box>
                  )}
                </FormHelperText>
              </FormControl>
            )}

            <Button
              variant='contained'
              color='secondary'
              fullWidth
              size='large'
              type='submit'
              disabled={
                otp.join('').length !== OTP_LENGTH ||
                status === OTPStatus.Submitting
              }
              sx={{
                height: '48px',
                mt: 2,
                '&.Mui-disabled': {
                  color: 'primary.contrastText',
                },
              }}
            >
              {status === OTPStatus.Submitting ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                'Verify Code'
              )}
            </Button>
          </form>
        </Box>

        {!emailAutosent && (
          <Button
            variant='text'
            onClick={onClick}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              p: 0,
              minWidth: '24px',
              opacity: 0.5,
              '&:hover': { opacity: 1 },
            }}
          >
            <ArrowBackIcon />
          </Button>
        )}

        <Typography variant='subtitle1' textAlign='center' sx={{ mt: 4 }}>
          Prefer Google Auth? Click Below.
        </Typography>
        <Box
          sx={{
            mt: 3,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <SSOButtons />
        </Box>
      </Box>
    </Fade>
  )
}
